// src/pages/ConsultarAlumnos.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/styles.css";
import "../styles/formularios.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";
import { resolveApiBaseList } from "../utils/apiBase";

// Parse a date string like 'YYYY-MM-DD' as local date (avoids UTC parse off-by-one)
function parseIsoDateToLocal(dateStr) {
  if (!dateStr) return null;
  // If it's exactly yyyy-mm-dd or starts with that
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }
  const parsed = new Date(dateStr);
  if (isNaN(parsed)) return null;
  return parsed;
}

const describirSexo = (valor = "") => {
  const normalizado = valor.toString().trim().toUpperCase();
  if (["H", "M", "MASCULINO"].includes(normalizado)) {
    return "Masculino";
  }
  if (["F", "FEMENINO"].includes(normalizado)) {
    return "Femenino";
  }
  return normalizado || "Sin especificar";
};

/* ======================= HOOK DE LÓGICA ======================= */
function useConsultarAlumnosLogica() {
  // 'buscador' | 'cumpleanios'
  const [tipoVista, setTipoVista] = useState("buscador");

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${path}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          const error = new Error(text || `Error ${response.status}`);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get("content-type") || "";
        const trimmed = text.trim();
        if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || "desconocido"})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error("No se pudo contactar al servidor.");
  }, [apiBases]);

  // === Buscador unificado (Nombre o CURP) ===
  const [query, setQuery] = useState("");
  const [sugerencias, setSugerencias] = useState([]); // [{IdAlumno, NombreCompleto, CURP}]
  const [loadingSug, setLoadingSug] = useState(false);
  const debounceRef = useRef(null);

  // Resultado individual
  const [alumno, setAlumno] = useState(null);
  const [loadingAlumno, setLoadingAlumno] = useState(false);

  // Cumpleañeros (formato {nombreCompleto, fecha})
  const [cumpleanieros, setCumpleanieros] = useState([]);
  const [loadingCumple, setLoadingCumple] = useState(false);

  // Autocompletado con debounce usando /grupos/buscar-alumnos
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setSugerencias([]);
      return;
    }

    // Si el usuario está buscando, mostramos la vista del buscador
    setTipoVista("buscador");

    debounceRef.current = setTimeout(async () => {
      setLoadingSug(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: "15" });
        const data = await fetchJsonWithFallback(`/grupos/buscar-alumnos?${params.toString()}`);
        setSugerencias(Array.isArray(data) ? data : []);
      } catch (error) {
        console.warn("⚠️ No se pudieron obtener sugerencias de alumnos:", error);
        setSugerencias([]);
      } finally {
        setLoadingSug(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [fetchJsonWithFallback, query]);

  // Cargar detalle por CURP (al seleccionar una sugerencia)
  const cargarAlumnoPorCURP = useCallback(async (curp) => {
    if (!curp) return;
    try {
      setLoadingAlumno(true);
      const data = await fetchJsonWithFallback(`/alumnos/por-curp/${encodeURIComponent(curp)}`);
      setAlumno(data || null);
    } catch (e) {
      console.error("❌ Error al obtener alumno por CURP:", e);
      Swal.fire("Error", "No se pudo obtener el alumno", "error");
    } finally {
      setLoadingAlumno(false);
    }
  }, [fetchJsonWithFallback]);

  const seleccionarSugerencia = useCallback((s) => {
    setQuery("");
    setSugerencias([]);
    cargarAlumnoPorCURP(s.CURP);
  }, [cargarAlumnoPorCURP]);

  const onKeyDownQuery = useCallback((e) => {
    if (e.key === "Enter" && sugerencias.length > 0) {
      e.preventDefault();
      seleccionarSugerencia(sugerencias[0]);
    }
  }, [sugerencias, seleccionarSugerencia]);

  // === Cumpleaños del mes ===
  const mostrarCumpleanios = useCallback(() => {
    setTipoVista("cumpleanios");
    (async () => {
      try {
        setLoadingCumple(true);
        const data = await fetchJsonWithFallback(`/alumnos/cumpleanios`);
        const lista = (Array.isArray(data) ? data : []).map((a) => {
          const fecha = a.FechaNacimiento ? parseIsoDateToLocal(a.FechaNacimiento) : null;
          const fechaFormateada = fecha
            ? fecha.toLocaleDateString("es-MX", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })
            : "N/A";
          return {
            nombreCompleto: `${a.Nombre ?? ""} ${a.ApellidoPaterno ?? ""} ${a.ApellidoMaterno ?? ""}`.trim(),
            fecha: fechaFormateada,
          };
        });
        setCumpleanieros(lista);
      } catch (e) {
        console.error("❌ Error cumple:", e);
        Swal.fire("Error", "No se pudieron obtener los cumpleaños del mes", "error");
        setCumpleanieros([]);
      } finally {
        setLoadingCumple(false);
      }
    })();
  }, [fetchJsonWithFallback, setTipoVista]);

  // Exportar Excel (cumpleañeros)
  const exportarExcel = useCallback(() => {
    if (!cumpleanieros || cumpleanieros.length === 0) return;
    const wb = XLSX.utils.book_new();
    const wsData = [["Nombre", "Fecha de nacimiento"]];
    cumpleanieros.forEach((r) => wsData.push([r.nombreCompleto, r.fecha]));
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Cumpleaños");
    XLSX.writeFile(wb, "cumpleanios_alumnos.xlsx");
  }, [cumpleanieros]);

  // Exportar PDF (cumpleañeros)
  const exportarPDF = useCallback(() => {
    const doc = new jsPDF();
    autoTable(doc, { html: "#tablaCumplesAlumnos", styles: { halign: "center" }, theme: "grid" });
    doc.save("cumpleanios_alumnos.pdf");
  }, []);

  const mostrarInfo = useMemo(() => !!alumno, [alumno]);

  return {
    // vista
    tipoVista,
    setTipoVista,
    // buscador
    query, setQuery, sugerencias, loadingSug, onKeyDownQuery, seleccionarSugerencia,
    // alumno
    alumno, loadingAlumno, mostrarInfo,
    // cumpleaños
    mostrarCumpleanios, cumpleanieros, loadingCumple, exportarExcel, exportarPDF,
  };
}

/* ======================= PÁGINA ======================= */
export default function ConsultarAlumnos() {
  const {
    // vista
    tipoVista, setTipoVista,
    // buscador
    query, setQuery, sugerencias, loadingSug, onKeyDownQuery, seleccionarSugerencia,
    // alumno
    alumno, loadingAlumno, mostrarInfo,
    // cumpleaños
    mostrarCumpleanios, cumpleanieros, loadingCumple, exportarExcel, exportarPDF,
  } = useConsultarAlumnosLogica();

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="text-center flex-grow-1">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consultar Alumnos</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Controles superiores */}
      <div className="mb-3 d-flex justify-content-center gap-3 flex-wrap">
        {/* Botón que se mantiene: Cumpleaños del Mes */}
        <button className="btn btn-outline-success" onClick={mostrarCumpleanios}>
          Cumpleaños del Mes
        </button>

        {/* (Opcional) botón para volver a vista de buscador cuando estés en cumples */}
        {tipoVista === "cumpleanios" && (
          <button className="btn btn-outline-secondary" onClick={() => setTipoVista("buscador")}>
            Volver al Buscador
          </button>
        )}
      </div>

      {/* Buscador unificado visible en vista 'buscador' */}
      {tipoVista === "buscador" && (
        <div className="mb-4">
          <label htmlFor="buscadorAlumno" className="form-label">Buscar alumno</label>
          <div className="position-relative">
            <input
              id="buscadorAlumno"
              className="form-control"
              placeholder="Buscar alumno por nombre o CURP"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDownQuery}
              autoComplete="off"
            />
            {query && (loadingSug || sugerencias.length > 0) && (
              <div
                className="list-group shadow position-absolute w-100"
                style={{ zIndex: 10, maxHeight: 260, overflowY: "auto" }}
              >
                {loadingSug && <div className="list-group-item text-muted">Buscando…</div>}
                {!loadingSug && sugerencias.length === 0 && (
                  <div className="list-group-item text-muted">Sin resultados</div>
                )}
                {!loadingSug &&
                  sugerencias.map((s) => (
                    <button
                      key={s.IdAlumno}
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => seleccionarSugerencia(s)}
                    >
                      <span>{s.NombreCompleto}</span>
                      <span className="badge bg-secondary">{s.CURP}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cargando alumno */}
      {loadingAlumno && <div className="alert alert-info">Cargando alumno…</div>}

      {/* Información del alumno */}
      {tipoVista === "buscador" && mostrarInfo && alumno && (
        <div id="infoAlumno" className="mt-4 bg-light p-3 rounded shadow-sm">
          <h5 className="mb-3">Información del Alumno</h5>
          <ul className="list-group">
            <li className="list-group-item">
              <strong>Nombre:</strong>{" "}
              {`${alumno.Nombre ?? ""} ${alumno.ApellidoPaterno ?? ""} ${alumno.ApellidoMaterno ?? ""}`.trim()}
            </li>
            <li className="list-group-item"><strong>CURP:</strong> {alumno.CURP ?? "N/A"}</li>
            <li className="list-group-item">
              <strong>Sexo:</strong>{" "}
              {describirSexo(alumno.Sexo)}
            </li>
            <li className="list-group-item"><strong>Correo:</strong> {alumno.Correo ?? "N/A"}</li>
            <li className="list-group-item"><strong>Tipo de Sangre:</strong> {alumno.TipoSangre ?? "N/A"}</li>
            <li className="list-group-item"><strong>Teléfono:</strong> {alumno.Telefono ?? "N/A"}</li>
          </ul>
        </div>
      )}

      {/* Cumpleaños del mes (se conserva tal cual) */}
      {tipoVista === "cumpleanios" && (
        <div className="bg-light p-4 rounded shadow-sm mt-4">
          <h6 className="mb-3 fw-semibold">Alumnos que cumplen años este mes</h6>

          {loadingCumple && <p className="text-muted">Cargando…</p>}

          {!loadingCumple && (
            <>
              {cumpleanieros.length > 0 ? (
                <>
                  <table className="table table-sm" id="tablaCumplesAlumnos">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Nombre</th>
                        <th>Fecha de Nacimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cumpleanieros.map((r, i) => (
                        <tr key={`${r.nombreCompleto}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{r.nombreCompleto}</td>
                          <td>{r.fecha}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-3 d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-success" onClick={exportarExcel}>
                      Exportar a Excel
                    </button>
                    <button className="btn btn-outline-danger" onClick={exportarPDF}>
                      Exportar a PDF
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-muted">No hay alumnos que cumplan años este mes.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
